import {
    convertDateArrayFromDate,
    convertDateFromDateArray,
} from '@starjack/utils';
import {
    BackendType,
    convertStarjackDateRangeToFrontend,
    FrontendType,
    Gender,
    MultiLangString,
    ObjectType,
    StarjackDate,
    StarjackDateRange,
} from './general';
import { ProductType } from '@/models/product';
import { Money } from '@/models/money';
import { v4 as uuid } from 'uuid';
import { LiftOperator } from '@/models/liftoperator';

/**
 * Tickets that can be ordered (unlike type Ticket, which is a ticket that has been ordered)
 */
export type OrderTicket<T extends ObjectType = FrontendType> = {
    availableContingent: number;
    productId: string;
    shortProductId: string;
    name: MultiLangString;
    liftOperatorName: MultiLangString;
    type: ProductType;
    duration: number;
    photoRequired: boolean;
    persons: {
        name: MultiLangString;
        birthday: StarjackDateRange<T>;
        count: number;
    }[];
    price: Money;
    originalPrice: Money;
    cancellationBlocked: boolean;
    issueValidity: StarjackDateRange<T>;
    validity: StarjackDateRange<T>;
    tags: string[];
    sortKey: number;
    infoText: MultiLangString;
    filterTags: string[];
    contingentsThreshold: number;
    contingent: number;
    startDay: StarjackDate<T>;
    externalConditionNames: MultiLangString[];
    maxTicketsPerStartDay: number;
    externalCode: string[];
    keycardCodes: string[];
    portalConfiguration: {
        keycard: boolean;
        barcode: boolean;
        pickup: boolean;
        internalProduction: boolean;
        mobileFlow: boolean;
    };
    integrationConfiguration: {
        keycard: boolean;
        barcode: boolean;
        pickup: boolean;
        internalProduction: boolean;
        mobileFlow: boolean;
    };
    b2bConfiguration: {
        keycard: boolean;
        barcode: boolean;
        pickup: boolean;
        internalProduction: boolean;
        mobileFlow: boolean;
    };
};

export const convertOrderTicketToFrontend = (
    p: OrderTicket<BackendType>
): OrderTicket => ({
    ...p,
    startDay: convertDateFromDateArray(p.startDay),
    validity: convertStarjackDateRangeToFrontend(p.validity),
    issueValidity: convertStarjackDateRangeToFrontend(p.issueValidity),
    persons: p.persons.map((person) => ({
        ...person,
        ...(!!person.birthday && {
            birthday: convertStarjackDateRangeToFrontend(person.birthday),
        }),
    })),
});

export type TicketHolder = {
    id: string;
    gender: Gender | '--';
    ticketId: string;
    firstname: string;
    lastname: string;
    birthDay: StarjackDate;
};

export const createTicketHolder = ({
    ticketId,
}: {
    ticketId: string;
}): TicketHolder => ({
    id: uuid(),
    gender: null,
    firstname: '',
    lastname: '',
    birthDay: null,
    ticketId,
});

/**
 * Ticket collection grouped by Ticket name (tickets with the same name but different personTypes are grouped together)
 * It also hold ticketHolders which is a list of customer data with a OrderTicket id reference (of the same collection)
 */
export type OrderTicketCollection = {
    // data that all concrete Tickets have in common
    name: OrderTicket['name'];
    duration: OrderTicket['duration'];
    type: OrderTicket['type'];
    startDay: OrderTicket['startDay'];
    validity: OrderTicket['validity'];
    filterTags: OrderTicket['filterTags'];
    infoText: OrderTicket['infoText'];
    photoRequired: OrderTicket['photoRequired'];
    sortKey: OrderTicket['sortKey'];

    numberOfTicketHolders: Readonly<number>;

    // map of productId and OrderTicket
    ticketMap: Record<
        string, // productId
        OrderTicket
    >;

    // key is productId of ticket which is hold by the ticketHolder
    ticketHoldersMap: Record<string, TicketHolder[]>;
};

export const getAllOrderTicketsFromCollection = (
    ticketCollection: OrderTicketCollection
): OrderTicket[] => Object.values(ticketCollection.ticketMap);

export const groupOrderTicketsByTicketName = (tickets: OrderTicket[]) => {
    const ticketCollectionMap: Record<string, OrderTicketCollection> =
        tickets.reduce<Record<string, OrderTicketCollection>>(
            (collectionContainer, currentTicket) => {
                if (collectionContainer[currentTicket.name.de]) {
                    const ticketCollection =
                        collectionContainer[currentTicket.name.de];
                    collectionContainer[currentTicket.name.de] = {
                        ...ticketCollection,
                        ticketMap: {
                            ...ticketCollection.ticketMap,
                            [currentTicket.productId]: currentTicket,
                        },

                        ticketHoldersMap: {
                            ...ticketCollection.ticketHoldersMap,
                            [currentTicket.productId]: [],
                        },
                    };
                } else {
                    collectionContainer[currentTicket.name.de] = {
                        name: currentTicket.name,
                        duration: currentTicket.duration,
                        type: currentTicket.type,
                        startDay: currentTicket.startDay,
                        validity: currentTicket.validity,
                        filterTags: currentTicket.filterTags,
                        infoText: currentTicket.infoText,
                        photoRequired: currentTicket.photoRequired,
                        sortKey: currentTicket.sortKey,

                        numberOfTicketHolders: 0,

                        ticketMap: {
                            [currentTicket.productId]: currentTicket,
                        },

                        ticketHoldersMap: {
                            [currentTicket.productId]: [],
                        },
                    };
                }

                return collectionContainer;
            },
            {}
        );

    return Object.values(ticketCollectionMap);
};

export const addTicketHolderToTicket = ({
    ticketCollection,
    ticketHolder,
}: {
    ticketCollection: OrderTicketCollection;
    ticketHolder: TicketHolder;
}): OrderTicketCollection => ({
    ...ticketCollection,

    numberOfTicketHolders: ticketCollection.numberOfTicketHolders + 1,

    ticketHoldersMap: {
        ...ticketCollection.ticketHoldersMap,
        [ticketHolder.ticketId]: [
            ...ticketCollection.ticketHoldersMap[ticketHolder.ticketId],
            ticketHolder,
        ],
    },
});

export const removeTicketHolder = (params: {
    ticketCollection: OrderTicketCollection;
    ticketHolder: TicketHolder;
}) => {
    const { ticketCollection, ticketHolder } = params;

    const currentTicketHolders =
        ticketCollection.ticketHoldersMap[ticketHolder.ticketId];
    return {
        ...ticketCollection,

        numberOfTicketHolders: ticketCollection.numberOfTicketHolders - 1,

        ticketHoldersMap: {
            ...ticketCollection.ticketHoldersMap,
            [ticketHolder.ticketId]: currentTicketHolders.filter(
                (cTH) => cTH.id !== ticketHolder.id
            ),
        },
    };
};

export const getPriceTotalFromTicketCollection = (
    ticketCollection: OrderTicketCollection
) => {
    return Object.values(ticketCollection.ticketHoldersMap).reduce<Money>(
        (collectionTotal, ticketHolders) => {
            return ticketHolders.reduce<Money>((ticketTotal, ticketHolder) => {
                return {
                    amount:
                        ticketTotal.amount +
                        ticketCollection.ticketMap[ticketHolder.ticketId].price
                            .amount,
                    currency: ticketTotal.currency,
                };
            }, collectionTotal);
        },
        { amount: 0, currency: 'EUR' }
    );
};

export type Discount = {
    discount: Money;
    birthday: StarjackDateRange<BackendType>;
};

export type SubmitOrderTicket = {
    confirmationNumber: string;
    identifications: { id: string }[];
    liftOperator: string;
    price: Money;
    productId: string;
    startDate: StarjackDate<BackendType>;
    priceCode?: string;
    discounts: Discount[];
};

export const createSubmitOrderTicketsFromTicketCollection = (
    ticketCollection: OrderTicketCollection,
    liftOperator: LiftOperator,
    confirmationNumber = ''
): SubmitOrderTicket[] => {
    return Object.values(ticketCollection.ticketHoldersMap).reduce<
        SubmitOrderTicket[]
    >((tickets, ticketHolders) => {
        ticketHolders.forEach((ticketHolder) => {
            tickets.push({
                confirmationNumber,
                identifications: [{ id: `@${ticketHolder.id}` }],
                liftOperator: liftOperator.id,
                price: ticketCollection.ticketMap[ticketHolder.ticketId].price,
                productId: ticketHolder.ticketId,
                startDate: convertDateArrayFromDate(
                    ticketCollection.ticketMap[ticketHolder.ticketId].startDay
                ),
                priceCode: null,
                discounts: [],
            });
        });

        return tickets;
    }, []);
};

export type TicketIdentification = {
    ref: string;

    birthday: StarjackDate<BackendType>;
    cardType: string;
    firstname: string;
    lastname: string;

    image: string;
    price: Money;
    depositAmount?: Money;
    gender: Gender;

    title: string;
    type: 'PICKUP';
};

export const createSubmitOrderIdentificationFromTicketCollection = (
    ticketCollection: OrderTicketCollection,
    liftOperator: LiftOperator
): TicketIdentification[] => {
    return Object.values(ticketCollection.ticketHoldersMap).reduce<
        TicketIdentification[]
    >((tickets, ticketHolders) => {
        ticketHolders.forEach((ticketHolder) => {
            tickets.push({
                ref: ticketHolder.id,
                firstname: ticketHolder.firstname,
                lastname: ticketHolder.lastname,
                birthday: convertDateArrayFromDate(ticketHolder.birthDay),
                type: 'PICKUP',
                depositAmount: liftOperator.pickupDepositAmount,

                image: null,
                price: {
                    amount: 0,
                    currency: 'EUR',
                },

                title: '-',
                gender: Gender.MALE,
                cardType: '',
            });
        });

        return tickets;
    }, []);
};

export type OrderDTO = {
    tickets: SubmitOrderTicket[];
    identifications: TicketIdentification[];
    shopProducts: [];
    vouchers: [];
    incertVoucherCodes?: [];
    address: Record<string, unknown>;
    invoiceAddress: Record<string, unknown> | null;
    shippingCosts: Money;
    profile: string;
    paymentMethod: string;
    psp: string;
    environment: 'B2B';
    reference?: string;
};

export type PayOrderBody = {
    orderId: string;
    token: string | null;
    saveProfile: boolean;
    successUrl: string;
    errorUrl: string;
};

export type PostPayOrderResponse = {
    orderId: string;
    status: string;
    location: string;
};
